/*
  Ma Cafetière Italienne — composants interactifs partagés.
  Custom elements autonomes : chaque instance nettoie ses écouteurs dans
  disconnectedCallback, ce qui couvre shopify:section:load / unload.
*/

(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!customElements.get('mci-carousel')) {
    class MciCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-mci-track]');
        if (!this.track) return;

        this.prevButton = this.querySelector('[data-prev]');
        this.nextButton = this.querySelector('[data-next]');
        this.dotsContainer = this.querySelector('[data-dots-container]');
        this.toggleButton = this.querySelector('[data-toggle]');
        this.pageCount = 0;

        this.onScroll = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.updateState();
          });
        };
        this.onPrev = () => this.go(-1);
        this.onNext = () => this.go(1);
        this.onBlockSelect = (event) => this.revealBlock(event.target);
        this.onPause = () => this.pause();
        this.onResume = () => this.resume();
        this.onToggle = () => (this.paused ? this.resume(true) : this.pause(true));

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.prevButton?.addEventListener('click', this.onPrev);
        this.nextButton?.addEventListener('click', this.onNext);
        this.toggleButton?.addEventListener('click', this.onToggle);
        document.addEventListener('shopify:block:select', this.onBlockSelect);

        this.resizeObserver = new ResizeObserver(() => this.refresh());
        this.resizeObserver.observe(this.track);

        this.setupAutoplay();
        this.refresh();
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.onScroll);
        this.prevButton?.removeEventListener('click', this.onPrev);
        this.nextButton?.removeEventListener('click', this.onNext);
        this.toggleButton?.removeEventListener('click', this.onToggle);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.resizeObserver?.disconnect();
        this.visibilityObserver?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
        clearTimeout(this.timer);
      }

      get step() {
        const slide = this.track.querySelector('[data-mci-slide]');
        if (!slide) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const slideWidth = slide.getBoundingClientRect().width + gap;
        const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / slideWidth));
        return slideWidth * perView;
      }

      get maxScroll() {
        return this.track.scrollWidth - this.track.clientWidth;
      }

      refresh() {
        const overflowing = this.maxScroll > 2;
        this.toggleAttribute('data-overflow', overflowing);
        const pages = overflowing ? Math.ceil(this.maxScroll / this.step - 0.05) + 1 : 0;
        if (pages !== this.pageCount) this.renderDots(pages);
        this.updateState();
      }

      renderDots(pages) {
        this.pageCount = pages;
        if (!this.dotsContainer) return;
        const template = this.dotsContainer.dataset.label || '[page] / [count]';
        const fragment = document.createDocumentFragment();
        for (let index = 0; index < pages; index++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'mci-carousel__dot';
          dot.setAttribute('aria-label', template.replace('[page]', index + 1).replace('[count]', pages));
          dot.addEventListener('click', () => this.scrollToPage(index));
          fragment.appendChild(dot);
        }
        this.dotsContainer.replaceChildren(fragment);
      }

      currentPage() {
        if (this.track.scrollLeft >= this.maxScroll - 2) return this.pageCount - 1;
        return Math.round(this.track.scrollLeft / this.step);
      }

      updateState() {
        const left = this.track.scrollLeft;
        if (this.prevButton) this.prevButton.disabled = left <= 2;
        if (this.nextButton) this.nextButton.disabled = left >= this.maxScroll - 2;
        if (!this.dotsContainer || !this.pageCount) return;
        const active = this.currentPage();
        [...this.dotsContainer.children].forEach((dot, index) => {
          dot.setAttribute('aria-current', index === active ? 'true' : 'false');
        });
      }

      scrollToLeft(left) {
        this.track.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }

      scrollToPage(index) {
        this.scrollToLeft(Math.min(index * this.step, this.maxScroll));
      }

      go(direction) {
        this.scrollToLeft(this.track.scrollLeft + direction * this.step);
      }

      revealBlock(target) {
        if (!(target instanceof Element) || !this.contains(target)) return;
        const slide = target.closest('[data-mci-slide]');
        if (!slide) return;
        this.pause();
        this.scrollToLeft(slide.offsetLeft - this.track.offsetLeft);
      }

      setupAutoplay() {
        const seconds = parseInt(this.dataset.autoplay, 10);
        if (!seconds || reducedMotion.matches || window.Shopify?.designMode) {
          this.removeAttribute('data-autoplay');
          return;
        }
        this.delay = seconds * 1000;
        this.paused = false;
        this.inView = false;
        ['mouseenter', 'focusin', 'touchstart'].forEach((type) =>
          this.addEventListener(type, this.onPause, { passive: true })
        );
        ['mouseleave', 'focusout'].forEach((type) => this.addEventListener(type, this.onResume));
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting;
          this.schedule();
        });
        this.visibilityObserver.observe(this);
      }

      schedule() {
        clearTimeout(this.timer);
        if (!this.delay || this.paused || this.userPaused || !this.inView) return;
        this.timer = setTimeout(() => {
          const atEnd = this.track.scrollLeft >= this.maxScroll - 2;
          atEnd ? this.scrollToLeft(0) : this.go(1);
          this.schedule();
        }, this.delay);
      }

      pause(byUser = false) {
        if (byUser) {
          this.userPaused = true;
          this.toggleButton?.setAttribute('aria-pressed', 'true');
        }
        this.paused = true;
        clearTimeout(this.timer);
      }

      resume(byUser = false) {
        if (byUser) {
          this.userPaused = false;
          this.toggleButton?.setAttribute('aria-pressed', 'false');
        }
        this.paused = false;
        this.schedule();
      }
    }

    customElements.define('mci-carousel', MciCarousel);
  }

  if (!customElements.get('mci-tabs')) {
    class MciTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll(':scope [role="tab"]')].filter(
          (tab) => tab.closest('mci-tabs') === this
        );
        this.onClick = (event) => {
          const tab = event.target.closest('[role="tab"]');
          if (tab && this.tabs.includes(tab)) this.select(tab);
        };
        this.onKeydown = (event) => {
          const index = this.tabs.indexOf(event.target);
          if (index === -1) return;
          const keys = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: this.tabs.length - 1 };
          if (!(event.key in keys)) return;
          event.preventDefault();
          const next = this.tabs[(keys[event.key] + this.tabs.length) % this.tabs.length];
          this.select(next);
          next.focus();
        };
        this.onBlockSelect = (event) => {
          if (!this.contains(event.target)) return;
          const tab = event.target.closest('[role="tab"]');
          const panel = event.target.closest('[role="tabpanel"]');
          const owner = this.tabs.includes(tab)
            ? tab
            : this.tabs.find((item) => panel && item.getAttribute('aria-controls') === panel.id);
          if (owner) this.select(owner);
        };
        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      select(activeTab) {
        this.tabs.forEach((tab) => {
          const selected = tab === activeTab;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
          const panel = document.getElementById(tab.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        });
      }
    }

    customElements.define('mci-tabs', MciTabs);
  }

  if (!customElements.get('mci-deferred-video')) {
    // Charge la vidéo d'ambiance uniquement si le contexte s'y prête
    // (largeur, préférence de mouvement, économie de données).
    class MciDeferredVideo extends HTMLElement {
      connectedCallback() {
        const video = this.querySelector('video');
        if (!video) return;
        const mobileAllowed = this.hasAttribute('data-mobile');
        const wideEnough = window.matchMedia('(min-width: 750px)').matches;
        const saveData = navigator.connection?.saveData;
        if (reducedMotion.matches || saveData || (!wideEnough && !mobileAllowed)) return;

        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
        });
        video.load();
        video.addEventListener('canplay', () => this.setAttribute('data-ready', ''), { once: true });
        video.play().catch(() => {});

        this.toggle = this.querySelector('[data-video-toggle]');
        this.onToggle = () => {
          const pause = !video.paused;
          pause ? video.pause() : video.play().catch(() => {});
          this.toggle.setAttribute('aria-pressed', pause ? 'true' : 'false');
        };
        this.toggle?.addEventListener('click', this.onToggle);
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener('click', this.onToggle);
      }
    }

    customElements.define('mci-deferred-video', MciDeferredVideo);
  }
})();
